import { Loader2 } from 'lucide-react';
import React from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { usePartnerProfile } from '../../../hooks/usePartnerSettings';
import type { Partner } from '../../../lib/api';
import { ActionButtons } from './ActionButtons';
import { DeactivateEmailSmsToggle } from './DeactivateEmailSmsToggle';
import { OnboardingToggle } from './OnboardingToggle';

/**
 * Engage Settings Tab Component
 * Settings and configuration for Engage features
 */
export const EngageSettingsTab: React.FC = () => {
  const { partner, isLoading, updateProfile, isUpdating } = usePartnerProfile();
  const [formData, setFormData] = React.useState<Partial<Partner>>({});

  React.useEffect(() => {
    if (partner) {
      setFormData({
        featureCustomizations: partner.featureCustomizations,
      });
    }
  }, [partner]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfile(formData);
  };

  /**
   * Handle changes to feature customizations
   * Updates the nested structure for enableFeature.engage.onboarding
   */
  const handleFeatureCustomizationChange = (enabled: boolean) => {
    setFormData(prev => ({
      ...prev,
      featureCustomizations: {
        ...prev.featureCustomizations,
        enableFeature: {
          ...prev.featureCustomizations?.enableFeature,
          engage: {
            ...prev.featureCustomizations?.enableFeature?.engage,
            onboarding: enabled,
          },
        },
      },
    }));
  };

  const handleDeactivateEmailSmsVisibilityChange = (enabled: boolean) => {
    setFormData(prev => ({
      ...prev,
      featureCustomizations: {
        ...prev.featureCustomizations,
        engage: {
          ...prev.featureCustomizations?.engage,
          showDeactivateEmailSmsButton: enabled,
        },
      },
    }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const handleCancel = () => {
    if (partner) {
      setFormData({
        featureCustomizations: partner.featureCustomizations
          ? {
              ...partner.featureCustomizations,
            }
          : undefined,
      });
    }
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-1">Engage Settings</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Configure Engage feature settings for your users
              </p>
            </div>

            <OnboardingToggle
              checked={formData.featureCustomizations?.enableFeature?.engage?.onboarding ?? true}
              onChange={handleFeatureCustomizationChange}
            />

            <DeactivateEmailSmsToggle
              checked={formData.featureCustomizations?.engage?.showDeactivateEmailSmsButton === true}
              onChange={handleDeactivateEmailSmsVisibilityChange}
            />
          </div>

          <ActionButtons isUpdating={isUpdating} onCancel={handleCancel} />
        </form>
      </CardContent>
    </Card>
  );
};
